/**
 * AssistantMessage component displays AI assistant messages with markdown rendering.
 * Left-aligned with assistant icon and streaming support.
 */
import type { ChatMessage } from "@nucleus/shared-types";
import { formatDistanceToNow } from "date-fns";
import { Bot, Loader2 } from "lucide-react";

import { MarkdownRenderer } from "./MarkdownRenderer";

import { cn } from "@/lib/utils";

interface AssistantMessageProps {
	/** The assistant message to display */
	message: ChatMessage;
	/** Whether to show the avatar */
	showAvatar?: boolean;
}

/**
 * AssistantMessage displays an AI assistant's response with markdown rendering.
 *
 * Features:
 * - Left-aligned layout
 * - Bot icon avatar
 * - Markdown content rendering
 * - Model name display (if available)
 * - Relative timestamp
 * - Streaming state with animated loader
 * @param props - Component props
 * @param props.message - The message object to display
 * @param props.showAvatar - Whether to show the avatar (false for grouped messages)
 */
export function AssistantMessage({ message, showAvatar = true }: AssistantMessageProps) {
	const isStreaming = message.status === "streaming";
	const isError = message.status === "error";

	return (
		<div className="flex gap-3">
			{/* Avatar */}
			<div
				className={cn(
					"flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted",
					!showAvatar && "invisible"
				)}
			>
				<Bot className="h-4 w-4 text-muted-foreground" />
			</div>

			{/* Message content - left-aligned */}
			<div className="flex max-w-[80%] flex-col gap-1">
				{/* Model name (if available) */}
				{message.model && (
					<div className="text-xs font-medium text-muted-foreground">{message.model}</div>
				)}

				{/* Message bubble with markdown */}
				<div
					className={cn(
						"rounded-2xl px-4 py-2.5",
						isError ? "border border-destructive bg-destructive/10" : "bg-muted"
					)}
				>
					{isError ? (
						<p className="text-sm text-red-400">
							{message.content || "An error occurred while generating the response."}
						</p>
					) : (
						<MarkdownRenderer content={message.content} />
					)}

					{/* Streaming indicator */}
					{isStreaming && (
						<div className="mt-2 flex items-center gap-2 text-muted-foreground">
							<Loader2 className="h-3 w-3 animate-spin" />
							<span className="text-xs">Generating...</span>
						</div>
					)}
				</div>

				{/* Timestamp */}
				<time className="text-xs text-muted-foreground">
					{formatDistanceToNow(message.createdAt, { addSuffix: true })}
				</time>
			</div>
		</div>
	);
}
